(function () {
  'use strict';

  function initializeMoonbloom(section) {
    if (!section || section.dataset.moonbloomInitialized === 'true') return;
    const config = window.MoonbloomConfig || {};
    const form = section.querySelector('[data-moonbloom-form]');
    if (!form) return;
    section.dataset.moonbloomInitialized = 'true';

    const addButton = form.querySelector('.add-to-cart');
    const hint = form.querySelector('[data-moonbloom-hint]');
    const totalPrice = form.querySelector('[data-moonbloom-total-price]');
    const errorEl = form.querySelector('[data-ls-cart-error]');

    // Reuse the theme's existing “More information” accordion markup and CSS.
    // Keep the handler scoped to Moonbloom so other product templates are unaffected.
    section.querySelectorAll('[data-moonbloom-colour-info]').forEach((button) => {
      button.addEventListener('click', () => {
        const panelId = button.getAttribute('aria-controls');
        const panel = panelId ? document.getElementById(panelId) : null;
        if (!panel || !section.contains(panel)) return;

        const opening = !panel.classList.contains('open');
        panel.classList.toggle('open', opening);
        button.setAttribute('aria-expanded', String(opening));
        button.textContent = opening
          ? (button.dataset.hideLabel || button.textContent)
          : (button.dataset.moreLabel || button.textContent);

        // Avoid downloading/initializing the video until the shopper opens it.
        if (opening) {
          panel.querySelectorAll('iframe[data-src]').forEach((frame) => {
            if (frame.hasAttribute('src') || !frame.dataset.src) return;
            frame.src = frame.dataset.src;
            frame.removeAttribute('data-src');
          });
        }
      });
    });

    const mainBanner = section.querySelector('.main-banner');
    const thumbnails = Array.from(section.querySelectorAll('.thumbnail'));

    const state = {
      mainOptions: [],
      mainVariant: null,
      companion: '',
      bloombudVariant: null,
      bloomspireVariant: null,
      vibration: '',
      straps: '',
      strapVariant: null,
    };

    function normalize(value) {
      return String(value ?? '').trim().toLowerCase();
    }

    function escapeHTML(value) {
      return String(value ?? '')
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }

    function formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: config.currency || 'EUR',
        }).format(amount);
      } catch (_) {
        return `${amount.toFixed(2)} ${config.currency || 'EUR'}`;
      }
    }

    function swapMainSrc(src) {
      if (!mainBanner || !src) return;
      mainBanner.classList.add('fade-in');
      const image = new Image();
      image.onload = () => {
        mainBanner.src = src;
        mainBanner.srcset = src;
        requestAnimationFrame(() => mainBanner.classList.remove('fade-in'));
      };
      image.onerror = () => mainBanner.classList.remove('fade-in');
      image.src = src;
    }

    thumbnails.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        thumbnails.forEach((item) => item.classList.remove('selected'));
        thumb.classList.add('selected');
        swapMainSrc(thumb.dataset.large || thumb.src);
      });
    });

    (function enableSwipe() {
      if (!mainBanner || !thumbnails.length) return;
      let index = Math.max(0, thumbnails.findIndex((item) => item.classList.contains('selected')));
      let startX = 0;
      let startY = 0;

      function goTo(nextIndex) {
        index = (nextIndex + thumbnails.length) % thumbnails.length;
        thumbnails.forEach((item) => item.classList.remove('selected'));
        thumbnails[index].classList.add('selected');
        swapMainSrc(thumbnails[index].dataset.large || thumbnails[index].src);
      }

      mainBanner.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
      }, { passive: true });

      mainBanner.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          goTo(index + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
    })();

    function activeButton(role) {
      return form.querySelector(`[data-role="${role}"].active`);
    }

    function selectedValue(role) {
      return activeButton(role)?.dataset.value || '';
    }

    function clearRole(role) {
      form.querySelectorAll(`[data-role="${role}"]`).forEach((button) => {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
      });
    }

    function selectButton(button) {
      const role = button.dataset.role;
      clearRole(role);
      button.classList.add('active');
      button.setAttribute('aria-pressed', 'true');
    }

    function renderPreview(target, sources) {
      if (!target) return;
      const valid = (Array.isArray(sources) ? sources : [sources]).filter((item) => item?.src);
      target.classList.toggle('is-visible', valid.length > 0);
      target.classList.toggle('is-double', valid.length > 1);
      target.innerHTML = valid.map((item) => `
        <img
          src="${escapeHTML(item.src)}"
          width="280"
          height="280"
          loading="lazy"
          alt="${escapeHTML(item.alt || '')}"
        >
      `).join('');
    }

    function renderButtonPreview(role, button) {
      const target = form.querySelector(`[data-preview-target="${role}"]`);
      if (!target || !button) return;
      renderPreview(target, [
        { src: button.dataset.preview || '', alt: button.dataset.previewAlt || '' },
        { src: button.dataset.preview2 || '', alt: button.dataset.previewAlt2 || '' },
      ]);
    }

    function mainOptionFrames() {
      return Array.from(form.querySelectorAll('[data-main-option-frame]'));
    }

    function mainSelectionComplete() {
      if (config.mainHasDefaultVariant) return true;
      return mainOptionFrames().every((frame) => frame.querySelector('[data-role="main-option"].active'));
    }

    function variantMatchesPartial(variant, selections) {
      return selections.every((value, index) => !value || normalize(variant.options?.[index]) === normalize(value));
    }

    function findMainVariant(requireAvailable = false) {
      if (config.mainHasDefaultVariant) {
        return (config.mainVariants || []).find((variant) => String(variant.id) === String(config.mainDefaultVariantId)) || null;
      }
      return (config.mainVariants || []).find((variant) => {
        if (requireAvailable && !variant.available) return false;
        return variantMatchesPartial(variant, state.mainOptions) && state.mainOptions.every(Boolean);
      }) || null;
    }

    function updateMainAvailability() {
      if (config.mainHasDefaultVariant) return;
      const frames = mainOptionFrames();
      frames.forEach((frame) => {
        const index = Number(frame.dataset.optionIndex);
        frame.querySelectorAll('[data-role="main-option"]').forEach((button) => {
          const test = [...state.mainOptions];
          test[index] = button.dataset.value || '';
          const possible = (config.mainVariants || []).some((variant) => variant.available && variantMatchesPartial(variant, test));
          button.disabled = !possible;
          button.setAttribute('aria-disabled', String(!possible));
          if (!possible && button.classList.contains('active')) {
            button.classList.remove('active');
            button.setAttribute('aria-pressed', 'false');
            state.mainOptions[index] = '';
          }
        });
      });
    }

    function updateMainPreview(index) {
      const frame = form.querySelector(`[data-main-option-frame][data-option-index="${index}"]`);
      const target = form.querySelector(`[data-main-option-preview="${index}"]`);
      const button = frame?.querySelector('[data-role="main-option"].active');
      if (!button || !target) {
        renderPreview(target, []);
        return;
      }

      const test = [...state.mainOptions];
      test[index] = button.dataset.value || '';
      const variant = (config.mainVariants || []).find((item) => item.image && variantMatchesPartial(item, test));
      renderPreview(target, variant?.image ? [{ src: variant.image, alt: button.dataset.value || config.mainProductTitle }] : []);
    }

    function hasBloombud() {
      return state.companion === 'bloombud' || state.companion === 'both';
    }

    function hasBloomspire() {
      return state.companion === 'bloomspire' || state.companion === 'both';
    }

    function setFrameVisibility(name, visible) {
      const frame = form.querySelector(`[data-custom-frame="${name}"]`);
      if (!frame) return;
      frame.hidden = !visible;
    }

    function clearDependentSelection(role, stateKey, previewRole = role) {
      clearRole(role);
      state[stateKey] = null;
      renderPreview(form.querySelector(`[data-preview-target="${previewRole}"]`), []);
    }

    function updateDependencies() {
      setFrameVisibility('bloombud-colour', hasBloombud());
      setFrameVisibility('bloomspire-colour', hasBloomspire());

      if (!hasBloombud()) clearDependentSelection('bloombud-variant', 'bloombudVariant');
      if (!hasBloomspire()) {
        clearDependentSelection('bloomspire-variant', 'bloomspireVariant');
        if (state.vibration === 'dual') {
          clearRole('vibration');
          state.vibration = '';
          renderPreview(form.querySelector('[data-preview-target="vibration"]'), []);
        }
      }

      const dualButton = form.querySelector('[data-role="vibration"][data-value="dual"]');
      if (dualButton) {
        const productConfigured = dualButton.dataset.configDisabled !== 'true';
        const shouldDisable = !hasBloomspire() || !productConfigured;
        dualButton.disabled = shouldDisable;
        dualButton.setAttribute('aria-disabled', String(shouldDisable));
      }


      const needsStrapPattern = state.straps === 'add';
      setFrameVisibility('strap-pattern', needsStrapPattern);
      if (!needsStrapPattern) clearDependentSelection('strap-variant', 'strapVariant');
    }

    function selectionComplete() {
      if (!mainSelectionComplete() || !state.mainVariant?.available) return false;
      if (!state.companion) return false;
      if (hasBloombud() && !state.bloombudVariant) return false;
      if (hasBloomspire() && !state.bloomspireVariant) return false;
      if (!state.vibration) return false;
      if (state.vibration === 'dual' && !hasBloomspire()) return false;
      if (!state.straps) return false;
      if (state.straps === 'add' && !state.strapVariant) return false;
      return true;
    }

    function selectedAddOnPrice() {
      let cents = 0;
      if (state.bloombudVariant) cents += Number(state.bloombudVariant.price || 0);
      if (state.bloomspireVariant) cents += Number(state.bloomspireVariant.price || 0);
      if (state.vibration === 'single') cents += Number(config.singleVibratorPrice || 0);
      if (state.vibration === 'dual') cents += Number(config.dualVibratorPrice || 0);
      if (state.strapVariant) cents += Number(state.strapVariant.price || 0);
      return cents;
    }

    function updatePrice() {
      if (!totalPrice) return;
      if (!state.mainVariant) {
        // Keep Shopify's server-rendered price until a purchasable colour is chosen.
        const availablePrices = (config.mainVariants || [])
          .filter((variant) => variant.available)
          .map((variant) => Number(variant.price))
          .filter(Number.isFinite);
        const startingPrice = availablePrices.length
          ? Math.min(...availablePrices)
          : Number(config.mainDefaultVariantPrice || 0);
        totalPrice.textContent = formatMoney(startingPrice + selectedAddOnPrice());
        return;
      }
      totalPrice.textContent = formatMoney(Number(state.mainVariant.price) + selectedAddOnPrice());
    }

    function updateSubmitState() {
      const complete = selectionComplete();
      if (addButton) {
        addButton.disabled = !complete;
        // Shared product-custom.css hides all .add-to-cart buttons by default.
        // Other Love Smiths builders explicitly reveal theirs when ready.
        addButton.style.visibility = complete ? 'visible' : 'hidden';
        addButton.style.pointerEvents = complete ? 'auto' : 'none';
      }
      if (hint) hint.style.display = complete ? 'none' : 'block';
    }

    // Price badges use live Shopify variant prices already embedded in the
    // buttons; no hard-coded prices or assumptions about future patterns.
    function availableVariantPrices(role) {
      return Array.from(form.querySelectorAll(`[data-role="${role}"]`))
        .filter((button) => !button.disabled)
        .map((button) => Number(button.dataset.price))
        .filter((price) => Number.isFinite(price) && price >= 0);
    }

    function lowestAvailablePrice(role) {
      const prices = availableVariantPrices(role);
      return prices.length ? Math.min(...prices) : null;
    }

    function showUpgradePrice(role, value, amount, prefix = '+') {
      const button = form.querySelector(`[data-role="${role}"][data-value="${value}"]`);
      if (!button) return;
      setPriceBadge(button, amount, prefix);
    }

    function setPriceBadge(button, cents, prefix = '+') {
      let badge = button.querySelector('[data-moonbloom-option-price]');
      if (cents == null || !Number.isFinite(Number(cents)) || Number(cents) <= 0) {
        badge?.remove();
        return;
      }
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'moonbloom-option-price';
        badge.dataset.moonbloomOptionPrice = '';
        button.appendChild(badge);
      }
      badge.textContent = `${prefix}${formatMoney(cents)}`;
    }

    function priceForCompanion(role, selectedVariant, selected) {
      return selected && selectedVariant
        ? Number(selectedVariant.price)
        : lowestAvailablePrice(role);
    }

    function updateUpgradePriceBadges() {
      const budPrice = priceForCompanion('bloombud-variant', state.bloombudVariant, hasBloombud());
      const spirePrice = priceForCompanion('bloomspire-variant', state.bloomspireVariant, hasBloomspire());
      showUpgradePrice('companion', 'bloombud', budPrice);
      showUpgradePrice('companion', 'bloomspire', spirePrice);
      showUpgradePrice('companion', 'both',
        budPrice == null || spirePrice == null ? null : budPrice + spirePrice);

      showUpgradePrice('vibration', 'single',
        form.querySelector('[data-role="vibration"][data-value="single"]:not([disabled])')
          ? Number(config.singleVibratorPrice) : null);
      // Show dual set's TOTAL add-on price, not the price of an extra vibrator.
      // Disabled dual selection still displays its price, if a product exists.
      showUpgradePrice('vibration', 'dual',
        config.dualVibratorVariantId ? Number(config.dualVibratorPrice) : null);

      const startingStrapPrice = lowestAvailablePrice('strap-variant');
      const strapPrice = state.straps === 'add' && state.strapVariant
        ? Number(state.strapVariant.price) : startingStrapPrice;
      showUpgradePrice('straps', 'add', strapPrice);

      // When a specific colour/pattern has a surcharge over its cheapest
      // available option, label only that difference (not a second full price).
      for (const role of ['bloombud-variant', 'bloomspire-variant', 'strap-variant']) {
        const baseline = lowestAvailablePrice(role);
        form.querySelectorAll(`[data-role="${role}"]`).forEach((button) => {
          const price = Number(button.dataset.price);
          setPriceBadge(button,
            baseline == null || !Number.isFinite(price) ? null : price - baseline);
        });
      }
    }

    function updateState() {
      updateMainAvailability();
      state.mainVariant = findMainVariant(true);
      updateDependencies();
      updateUpgradePriceBadges();
      updatePrice();
      updateSubmitState();

      if (state.mainVariant?.image && mainSelectionComplete()) {
        swapMainSrc(state.mainVariant.image);
      }
    }

    function parseVariantButton(button) {
      return {
        id: Number(button.dataset.variantId),
        price: Number(button.dataset.price || 0),
        value: button.dataset.value || '',
      };
    }

    form.addEventListener('click', (event) => {
      const button = event.target.closest('.option-btn');
      if (!button || !form.contains(button) || button.disabled) return;

      const role = button.dataset.role;
      if (!role) return;

      selectButton(button);

      if (role === 'main-option') {
        const index = Number(button.dataset.optionIndex);
        state.mainOptions[index] = button.dataset.value || '';
        updateMainPreview(index);
      } else if (role === 'companion') {
        state.companion = button.dataset.value || '';
        if (state.companion === 'none') {
          renderPreview(form.querySelector('[data-preview-target="companion"]'), []);
        } else {
          renderButtonPreview(role, button);
        }
      } else if (role === 'bloombud-variant') {
        state.bloombudVariant = parseVariantButton(button);
        renderButtonPreview(role, button);
      } else if (role === 'bloomspire-variant') {
        state.bloomspireVariant = parseVariantButton(button);
        renderButtonPreview(role, button);
      } else if (role === 'vibration') {
        state.vibration = button.dataset.value || '';
        if (state.vibration === 'none') {
          renderPreview(form.querySelector('[data-preview-target="vibration"]'), []);
        } else {
          renderButtonPreview(role, button);
        }

      } else if (role === 'straps') {
        state.straps = button.dataset.value || '';
        if (state.straps === 'none') {
          renderPreview(form.querySelector('[data-preview-target="straps"]'), []);
        } else {
          renderButtonPreview(role, button);
        }
      } else if (role === 'strap-variant') {
        state.strapVariant = parseVariantButton(button);
        renderButtonPreview(role, button);
      }

      updateState();
    });

    function bundleId() {
      return `moonbloom-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }

    function buildItems() {
      const id = bundleId();
      const items = [];
      const companionLabel = state.companion === 'both'
        ? 'Bloombud + Bloomspire'
        : state.companion === 'bloombud'
          ? 'Bloombud'
          : state.companion === 'bloomspire'
            ? 'Bloomspire'
            : 'None';

      const vibrationLabel = state.vibration === 'dual'
        ? '2 synchronized vibrators'
        : state.vibration === 'single'
          ? '1 vibrator'
          : 'None';

      items.push({
        id: Number(state.mainVariant.id),
        quantity: 1,
        properties: {
          _moonbloom_bundle_id: id,
          Companion: companionLabel,
          Vibration: vibrationLabel,
          Straps: state.straps === 'add' ? (state.strapVariant?.value || 'Added') : 'None',
        },
      });

      if (state.bloombudVariant) {
        items.push({
          id: Number(state.bloombudVariant.id),
          quantity: 1,
          properties: { _moonbloom_bundle_id: id, _moonbloom_component: 'Bloombud' },
        });
      }

      if (state.bloomspireVariant) {
        items.push({
          id: Number(state.bloomspireVariant.id),
          quantity: 1,
          properties: { _moonbloom_bundle_id: id, _moonbloom_component: 'Bloomspire' },
        });
      }

      if (state.vibration === 'single' && config.singleVibratorVariantId) {
        items.push({
          id: Number(config.singleVibratorVariantId),
          quantity: 1,
          properties: {
            _moonbloom_bundle_id: id,
            _moonbloom_component: 'Vibrator',
          },
        });
      }

      if (state.vibration === 'dual' && config.dualVibratorVariantId) {
        items.push({
          id: Number(config.dualVibratorVariantId),
          quantity: 1,
          properties: { _moonbloom_bundle_id: id, _moonbloom_component: 'Dual synchronized vibrator set' },
        });
      }

      if (state.strapVariant) {
        items.push({
          id: Number(state.strapVariant.id),
          quantity: 1,
          properties: { _moonbloom_bundle_id: id, _moonbloom_component: 'Strap set' },
        });
      }

      return items;
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!selectionComplete() || !state.mainVariant) return;

      if (errorEl) {
        errorEl.hidden = true;
        errorEl.textContent = '';
      }

      addButton?.classList.add('loading');
      if (addButton) addButton.disabled = true;

      try {
        if (!window.LSAjaxCart?.addItems) {
          throw new Error(config.cartError || 'Unable to update the cart.');
        }

        await window.LSAjaxCart.addItems(buildItems(), {
          open: true,
          trigger: addButton,
          source: 'moonbloom-builder',
        });
      } catch (error) {
        console.error('[Love Smiths] Moonbloom add-to-cart error:', error);
        if (errorEl) {
          errorEl.textContent = error?.message || config.cartError || 'Unable to update the cart.';
          errorEl.hidden = false;
        }
      } finally {
        addButton?.classList.remove('loading');
        updateSubmitState();
      }
    });

    const shippingToggle = section.querySelector('.shipping-toggle');
    const shippingContent = section.querySelector('.shipping-content');
    shippingToggle?.addEventListener('click', () => {
      const open = shippingToggle.getAttribute('aria-expanded') === 'true';
      shippingToggle.setAttribute('aria-expanded', String(!open));
      shippingContent?.classList.toggle('open', !open);
    });

    if (config.mainHasDefaultVariant) {
      state.mainVariant = findMainVariant(true);
    } else {
      state.mainOptions = new Array(mainOptionFrames().length).fill('');
    }

    form.querySelectorAll('[data-role="vibration"][data-value="dual"]').forEach((button) => {
      if (button.disabled) button.dataset.configDisabled = 'true';
    });

    updateState();
  }

  function bootMoonbloom(root = document) {
    root.querySelectorAll('.moonbloom-product[data-section-id]').forEach(initializeMoonbloom);
    if (root.matches?.('.moonbloom-product[data-section-id]')) initializeMoonbloom(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bootMoonbloom(), { once: true });
  } else {
    bootMoonbloom();
  }
  document.addEventListener('shopify:section:load', (event) => bootMoonbloom(event.target));
})();
